import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Search, LifeBuoy } from 'lucide-react';
import SEO from '../components/SEO';

/**
 * GIWAHS — 404 · Not Found
 *
 * Branded, calm, premium recovery surface for any unmatched route.
 * Preserves D-095 identity: obsidian background, Founder Gold overline,
 * platinum ivory copy, hairline dividers. Offers three deliberate paths
 * back into the platform (Home, Factory Directory, Contact) plus a
 * shortcut into the global search on desktop.
 *
 * No new business logic, no telemetry side-effects, no auth surface.
 * Uses `noindex` — search engines should never crawl 404 URLs.
 */
const NotFound = () => {
  const location = useLocation();

  return (
    <div data-testid="not-found-page" className="surface-obsidian">
      <SEO title="Page not found — GIWAHS" description="The page you are looking for could not be found." noindex />
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:grid-cols-12 md:px-8 md:py-28">
          <div className="md:col-span-7">
            <span className="label-overline text-gold">/ 404 · Not Found</span>
            <h1
              data-testid="not-found-heading"
              className="mt-4 font-display tracking-tighter text-platinum"
              style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
            >
              This page has wandered off.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-platinum-2">
              The URL you followed no longer exists on GIWAHS — or it never did.
              No cause for alarm: the platform, your saved factories, your compare lists,
              and any RFQs you have filed are all intact.
            </p>
            <div className="mt-4 font-mono-data text-[11px] uppercase tracking-[0.16em] text-muted-warm">
              REQUEST · <span data-testid="not-found-path" className="text-platinum-2">{location.pathname}</span>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/"
                data-testid="not-found-cta-home"
                className="btn-giwahs btn-giwahs-primary"
              >
                Return home <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link
                to="/factories"
                data-testid="not-found-cta-factories"
                className="btn-giwahs btn-giwahs-secondary"
              >
                Open the factory directory
              </Link>
              <Link
                to="/contact"
                data-testid="not-found-cta-contact"
                className="btn-giwahs btn-giwahs-outline-gold"
              >
                <LifeBuoy size={14} strokeWidth={1.5} /> Contact support
              </Link>
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="giwahs-auth-panel p-6 md:p-8">
              <span className="label-overline text-gold">Where next</span>
              <h2 className="mt-3 font-display text-2xl tracking-tight text-platinum">Popular destinations.</h2>
              <ul className="mt-6 divide-y divide-[rgba(200,166,74,0.14)]">
                {[
                  { to: '/products', label: 'Products', hint: 'Editorial catalog' },
                  { to: '/factories', label: 'Factories', hint: 'Verified suppliers' },
                  { to: '/intelligence', label: 'Intelligence', hint: 'Brand ↔ factory brief' },
                  { to: '/trust', label: 'Trust Centre', hint: 'Security, privacy, compliance' },
                  { to: '/rfq', label: 'Request a Quote', hint: 'Guided 5-step brief' },
                  { to: '/track', label: 'Track an RFQ', hint: 'Public lookup by reference' },
                ].map((it) => (
                  <li key={it.to}>
                    <Link
                      to={it.to}
                      data-testid={`not-found-link-${it.to.slice(1).replace(/\//g, '-') || 'home'}`}
                      className="group flex items-center justify-between gap-4 py-3 transition-colors hover:text-gold"
                    >
                      <span className="flex flex-col">
                        <span className="text-platinum group-hover:text-gold transition-colors">{it.label}</span>
                        <span className="mt-0.5 font-mono-data text-[11px] uppercase tracking-[0.12em] text-muted-warm">{it.hint}</span>
                      </span>
                      <ArrowRight size={14} strokeWidth={1.5} className="text-muted-warm group-hover:text-gold transition-colors" />
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex items-start gap-2 border-t border-hairline pt-4 text-xs text-muted-warm">
                <Search size={12} strokeWidth={1.5} className="mt-0.5 text-gold" />
                <span>Tip — press <span className="font-mono-data text-platinum-2">⌘K</span> anywhere on the platform to open global search.</span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
